/* NPM */
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { PureComponent } from 'react';
/* STORES */
import PollConnectStore from 'stores/PollConnectStore';
/* COMPONENTS */
import { Button, DataTable, Flex, MDIIcon } from '@solinkcorp/solink-components';
import PollProgress from '../../PollProgress/PollProgress';
/* UTILS */
import CommonUtil from 'utils/CommonUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import {
  getDownloadedTooltip,
  getDownloadExpiration,
  getDownloadSize,
} from '../utils/downloadStreamUtils';
/* CONSTANTS */
import { type CallhomeStream, type DeviceEntity, type DeviceId } from '@solinkcorp/api-types';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { type AuthLocation } from '@solinkcorp/vd-common/lib/esm/auth/authUtils';
import { type IEvent } from 'clients/PlatformApi';
import {
  CONNECT_DOWNLOAD_STATUS_CANCELLED,
  CONNECT_DOWNLOAD_STATUS_DOWNLOADED,
  CONNECT_DOWNLOAD_STATUS_ENQUEUED,
  CONNECT_DOWNLOAD_STATUS_EXPIRED,
  CONNECT_DOWNLOAD_STATUS_FAILED,
  CONNECT_DOWNLOAD_STATUS_READY,
  type ConnectDownloadStatus,
  POLLING_DOWNLOAD_STATUS_ERROR,
} from 'vd-constants';
import { type SavePanelCamera } from '../types';
/* ICONS */
import Download from '@mdi/svg/svg/download.svg?react';
import useLocale from 'src/hooks/useLocale';

export interface DownloadStream extends CallhomeStream {
  downloadCount: number;
  downloaded: { [key: string]: number[] };
  expiration: number;
  percent: number;
  state: ConnectDownloadStatus | typeof POLLING_DOWNLOAD_STATUS_ERROR;
  toReady: boolean;
}

interface DownloadDataItem {
  cameraName: string;
  downloadDone: boolean;
  downloadError: boolean;
  downloadExpired: boolean;
  downloadExpiry: string;
  downloadFailed: boolean;
  downloadSize: string;
  downloadStream: DownloadStream;
  downloadedTooltip: string;
  fileName: string;
  idx: number;
  isBusy: boolean;
  link: string;
  percent: number;
  preparing: boolean;
  preparingEnque: boolean;
  shouldRenderExpiry: boolean;
  sizeMB: number;
  streamId: string;
}

interface TableColumn {
  header: string;
  id: string;
  props: any[];
  renderer: any;
  sortable?: boolean;
  width?: number;
}

interface DownloadDetailsProps {
  downloadStreamIds: string[];
  downloadStreams: { [key: string]: DownloadStream };
  editMode: boolean;
  event: IEvent;
  generateProgressName: (eventId: string, streamId: string | null) => string;
  areDevicesLocal: Record<string, boolean | undefined>;
  alwaysLocal: boolean;
  onDownloadDetailRowButtonClick: (
    link: string,
    device: AuthLocation,
    streamId: string,
    metadata: { fileName: string; sizeMB: number },
  ) => void;
  selectedCameras: { [key: string]: SavePanelCamera };
  devices: DeviceEntity[] | undefined;
  accessibleAddresses: Record<DeviceId, string>;
}

interface DownloadDetailsInnerProps extends DownloadDetailsProps {
  locale: LocaleDefinition;
}

interface DownloadDetailsState {
  downloadData: DownloadDataItem[];
}

class DownloadDetails extends PureComponent<DownloadDetailsInnerProps, DownloadDetailsState> {
  private tableColumns: TableColumn[];
  constructor(props: DownloadDetailsInnerProps) {
    super(props);
    const { editMode, areDevicesLocal, alwaysLocal, locale, onDownloadDetailRowButtonClick } =
      this.props;

    this.tableColumns = [
      {
        header: locale.download.downloadDetails,
        id: 'downloadDetails',
        props: ['cameraName', 'shouldRenderExpiry', 'downloadExpiry'],
        renderer: ({
          data: { cameraName, shouldRenderExpiry, downloadExpiry },
        }: {
          data: { cameraName: string; shouldRenderExpiry: boolean; downloadExpiry: string };
        }) => (
          <div>
            <div>{cameraName}</div>
            {shouldRenderExpiry && <div className="download-expiry">{downloadExpiry}</div>}
          </div>
        ),
        sortable: false,
      },
      {
        header: locale.general.size,
        id: 'size',
        props: ['downloadSize'],
        renderer: ({ data: { downloadSize } }: { data: { downloadSize: string } }) => (
          <div>{downloadSize}</div>
        ),
        sortable: false,
      },
      {
        header: locale.download.localOnly,
        id: 'localOnly',
        props: [
          'deviceId',
          'preparingEnque',
          'preparing',
          'percent',
          'downloadDone',
          'link',
          'streamId',
          'fileName',
          'sizeMB',
          'downloadedTooltip',
          'downloadExpired',
          'downloadFailed',
          'downloadError',
        ],
        renderer: ({
          data: {
            device,
            preparingEnque,
            preparing,
            percent,
            downloadDone,
            link,
            streamId,
            fileName,
            sizeMB,
            downloadedTooltip,
            downloadExpired,
            downloadFailed,
            downloadError,
          },
        }: {
          data: {
            device: AuthLocation;
            preparingEnque: boolean;
            preparing: boolean;
            percent: number;
            downloadDone: boolean;
            link: string;
            streamId: string;
            fileName: string;
            sizeMB: number;
            downloadedTooltip: string;
            downloadExpired: boolean;
            downloadFailed: boolean;
            downloadError: boolean;
          };
        }) => (
          <div>
            {preparingEnque && (
              <div className="download-state flex-end">
                <div>{locale.download.statusPreparing}</div>
                <div className="value">0%</div>
              </div>
            )}
            {preparing && PollConnectStore.isProcessing(event, streamId) && (
              <div className="download-state flex-end">
                <div>{locale.download.statusPreparing}</div>
                <PollProgress displayProgressBar={false} locale={locale} percent={percent} />
              </div>
            )}
            {!downloadDone && !downloadExpired && !downloadFailed && !downloadError && (
              <Flex
                className="download-state"
                justifyContent="flex-end"
              >{`${locale.general.loading}...`}</Flex>
            )}
            {downloadDone && (
              <div className="download-state flex-end">
                <div className="download-state-msg">
                  <Flex className="download-buttons" justifyContent="flex-end">
                    <Button
                      className="download-button"
                      disabled={editMode || !(areDevicesLocal[device.id] || alwaysLocal)}
                      icon={<MDIIcon icon={Download} />}
                      onClick={() =>
                        onDownloadDetailRowButtonClick(link, device, streamId, {
                          fileName,
                          sizeMB,
                        })
                      }
                      text={locale.download.download}
                      tooltip={downloadedTooltip || locale.download.infoNotDownloaded}
                    />
                  </Flex>
                </div>
              </div>
            )}
            {downloadExpired && (
              <div className="download-state flex-end">{locale.download.statusExpired}</div>
            )}
            {downloadFailed && (
              <div className="download-state flex-end">
                <div className="download-error">
                  <div>{`${locale.download.statusFailed}`}</div>
                  <div className="download-error-message">{`${locale.error.downloadPrepare}`}</div>
                </div>
              </div>
            )}
            {downloadError && (
              <div className="download-state flex-end">
                <div className="download-error">
                  <div>{`${locale.download.statusError}`}</div>
                  <div className="download-error-message">{`${locale.error.downloadFailed}`}</div>
                </div>
              </div>
            )}
          </div>
        ),
        sortable: false,
      },
    ];
    this.state = {
      downloadData: this.getDownloadTableData(),
    };
  }

  componentDidUpdate(prevProps: DownloadDetailsProps) {
    const { downloadStreamIds, downloadStreams, event, selectedCameras } = this.props;

    if (
      !isEqual(downloadStreams, prevProps.downloadStreams) ||
      !CommonUtil.areObjectsEqual(event, prevProps.event) ||
      !CommonUtil.areArraysStrictlyEqual(downloadStreamIds, prevProps.downloadStreamIds) ||
      !CommonUtil.areArraysStrictlyEqual(selectedCameras, prevProps.selectedCameras)
    ) {
      this.setState({
        downloadData: this.getDownloadTableData(),
      });
    }
  }

  render() {
    const { downloadData } = this.state;
    const { locale } = this.props;
    return (
      <div className="download-panel">
        <DataTable
          columns={this.tableColumns}
          data={downloadData}
          defaultSorting={{
            isDescending: true,
            prop: 'cameraName',
          }}
          locale={locale}
        />
      </div>
    );
  }

  getDownloadTableData = () => {
    const {
      downloadStreamIds,
      downloadStreams,
      event,
      selectedCameras,
      devices,
      accessibleAddresses,
      locale,
    } = this.props;
    const { id: eventId } = event;

    const downloadData = Object.keys(selectedCameras).map((camId, idx) => {
      const camera: SavePanelCamera = selectedCameras[camId];
      const { streams, name: cameraName } = camera;
      const stream = streams.find((strm) => {
        return downloadStreamIds.includes(strm.id);
      });
      const { id: streamId } = stream as {
        id: string;
        name: string;
        earliestSegmentDate?: number | undefined;
        latestSegmentDate?: number | undefined;
      };
      const downloadStream = downloadStreams[streamId];

      // Get properties/details
      const { link, alias, sizeMB, fileName } = PollConnectStore.getDownloadProperties(
        event,
        stream,
      );

      const { percent, state } = downloadStream;
      const downloadExpiry = getDownloadExpiration(downloadStream, locale);
      const downloadedTooltip = getDownloadedTooltip(downloadStream, locale);
      const { isBusy } = this.getProgressState(eventId, streamId, alias);
      const downloadSize =
        !isBusy && !isNil(sizeMB) && !isNaN(sizeMB) ? getDownloadSize(sizeMB) : '';
      const shouldRenderExpiry =
        !isEmpty(downloadExpiry) &&
        !isBusy &&
        state !== CONNECT_DOWNLOAD_STATUS_FAILED &&
        state !== CONNECT_DOWNLOAD_STATUS_CANCELLED;
      const preparingEnque = isBusy && state === CONNECT_DOWNLOAD_STATUS_ENQUEUED;
      const preparing = isBusy && state !== CONNECT_DOWNLOAD_STATUS_ENQUEUED;
      const downloadDone =
        !isBusy &&
        (state === CONNECT_DOWNLOAD_STATUS_READY || state === CONNECT_DOWNLOAD_STATUS_DOWNLOADED);
      const downloadExpired = !isBusy && state === CONNECT_DOWNLOAD_STATUS_EXPIRED;
      const downloadFailed = !isBusy && state === CONNECT_DOWNLOAD_STATUS_FAILED;
      const downloadError = !isBusy && state === POLLING_DOWNLOAD_STATUS_ERROR;

      const deviceId = camera.deviceId;
      const device = devices?.find((device) => device.id === deviceId);
      const authDevice: AuthLocation = {
        id: deviceId,
        name: device?.name ?? '',
        accessibleAddress: accessibleAddresses[deviceId] || '',
      };

      return {
        cameraName,
        device: authDevice,
        downloadDone,
        downloadedTooltip,
        downloadError,
        downloadExpired,
        downloadExpiry,
        downloadFailed,
        downloadSize,
        downloadStream,
        fileName,
        idx,
        isBusy,
        link,
        percent,
        preparing,
        preparingEnque,
        shouldRenderExpiry,
        sizeMB,
        streamId,
      };
    });

    return downloadData;
  };

  getProgressState(eventId: string, streamId: string, alias: string) {
    const isBusy = PollConnectStore.isProcessing(eventId, streamId);
    const canCancel =
      !isEmpty(alias) &&
      PollConnectStore.canCancel(eventId, streamId) &&
      FeaturesUtil.canCancelDownload();

    return { isBusy, canCancel };
  }
}

const ComposedDownloadDetails = (props: DownloadDetailsProps) => {
  const locale = useLocale();
  return <DownloadDetails {...props} locale={locale} />;
};

export default ComposedDownloadDetails;
